import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { PermissionService } from '../services/permission.service';

/**
 * Guards the "View As" picker.
 *
 * Deliberately checks the user's **assigned** roles rather than the role they
 * are currently acting as (`PermissionGuard`'s `requiredGroups` reads the
 * acting-as permission). Two reasons:
 *   1. While impersonating, the acting-as groups are the *target's* — using them
 *      would lock the user out of the picker they need to switch or stop.
 *   2. Impersonation authority is a property of who you are, not who you are
 *      currently viewing as.
 *
 * Roles that may impersonate: Academic Appointment Admin/User (incl. CUIMC),
 * reporting-unit admins, and department admins. Everyone else — notably the
 * Vice Provost for Faculty Affairs and academic officers — is sent to the
 * dashboard.
 */
export const impersonationGuard: CanActivateFn = () => {
  const permissionService = inject(PermissionService);
  const router = inject(Router);

  // Roles still loading — let it through rather than bouncing mid-resolve; the
  // per-option checks and the backend validate call remain in force.
  if (permissionService.rolesLoading()) return true;
  if (permissionService.apiUserRoles().length === 0) return true;

  if (permissionService.canImpersonate()) return true;

  router.navigate(['/dashboard']);
  return false;
};
